import { Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { CapabilityIdEnum } from 'src/app/shared/capabilities-and-permissions';
import { AbstractComponentWithScrollContainer } from 'src/app/shared/components/abstract-component-with-scroll-container';
import { BasicService } from 'src/app/shared/services/basic.service';
import { KeyboardShowHideService } from 'src/app/shared/services/keyboard-show-hide.service';
import { ScrollService } from 'src/app/shared/services/scroll.service';

import { TaskOrActionType } from '../shared/model';
import { TaskProcessContext } from '../shared/task-process-context.class';
import { TaskProcessModalsService } from '../shared/task-process-modals.service';
import { TaskProcessUpdatesService } from '../shared/task-process-updates.service';
import { TaskProcessService } from '../shared/task-process.service';

@Component({
  selector: 'app-help-requests-modal',
  templateUrl: './help-requests-modal.component.html',
  styleUrls: ['./help-requests-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class HelpRequestsModalComponent extends AbstractComponentWithScrollContainer implements OnInit, OnDestroy {
  @Input() taskId: number;
  @Input() helpReqsAndAnswers: any[];
  @Input() taskProcessContext: TaskProcessContext;

  placeholderLabel = '';
  buttonLabel = '';
  TaskOrActionType = TaskOrActionType;
  messageControl = new FormControl('', Validators.required);
  canAnswerHelpRequest: boolean;
  isTaskStateHelpRequested: boolean;
  subscriptions = new Subscription();

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private keyboardShowHideService: KeyboardShowHideService,
    private scrollService: ScrollService,
    private taskProcessModalsService: TaskProcessModalsService,
    private taskProcessService: TaskProcessService,
    private taskProcessUpdatesService: TaskProcessUpdatesService
  ) {
    super();
  }

  ngOnInit() {
    this.determineIfHelpRequestOrAnswer();
    this.subscriptions.add(
      this.taskProcessUpdatesService.newTaskOrAction$.subscribe(() => this.determineIfHelpRequestOrAnswer())
    );
  }

  private determineIfHelpRequestOrAnswer() {
    if (this.taskProcessContext) {
      const { site, isReadOnlyMode, allHelpRequestAndResponseIds } = this.taskProcessContext;

      if (isReadOnlyMode) {
        this.messageControl.disable();
      } else {
        this.messageControl.enable();
      }
      const siteId = site.id;

      this.isTaskStateHelpRequested =
        allHelpRequestAndResponseIds.length &&
        allHelpRequestAndResponseIds.slice().pop().endsWith(TaskOrActionType.HelpRequest);

      let placeholderKey = 'm/task-process/help-requests/placeholder';
      let submitBtnKey = 'm/task-process/help-requests/ask-for-help';

      if (this.isTaskStateHelpRequested) {
        placeholderKey = this.basicService.translate('m/task-process/help-requests/your-answer-placeholder');
        submitBtnKey = this.basicService.translate('m/task-process/help-requests/submit-answer');
        this.canAnswerHelpRequest = this.alcmeonContextService.hasCapability(
          siteId,
          CapabilityIdEnum.ProcessHelpRequests
        );
      }
      this.placeholderLabel = this.basicService.translate(placeholderKey);
      this.buttonLabel = this.basicService.translate(submitBtnKey);
    }
  }

  /**
   * Overriden and called from the extended class when the ionContent part is loaded.
   */
  onScrollContainerReady() {
    this.scrollService.scrollToBottom(this);

    try {
      // The text area related to answer to an help request should be focused by default when this part is opened.
      const element: any = this.ionContent['el'].querySelector('textarea#answer-message');
      element?.focus();
    } catch {
      // Do nothing
    }
  }

  ngOnDestroy() {
    this.keyboardShowHideService.clearScrollElementAndTextInputElement();
    this.subscriptions?.unsubscribe();
  }

  dismissModal(goBackToTaskListOptions?: { response; queueId }) {
    this.subscriptions?.unsubscribe();
    this.taskProcessModalsService.dismissTaskProcessPageModal(goBackToTaskListOptions);
  }

  onTextareaClicked(event) {
    this.keyboardShowHideService.setScrollElementAndTextInputElement(this.scrollContainer, event.target);
  }

  submit(helpRequestId?: number) {
    const message = this.messageControl.value;
    const call = helpRequestId
      ? this.taskProcessService.answerHelpRequest(this.taskId, {
          help_request_id: helpRequestId,
          comment: message,
        })
      : this.taskProcessService.requestHelp(this.taskId, { message });

    call.subscribe(response => {
      this.dismissModal({ response, queueId: null }); // back to the tasks list page
    });
  }
}
